import { type DefaultRootState } from '@databases/redux';
import { type DatabaseSnapshot } from '@databases/redux-database-adapter';
import { type Database } from '@databases/types/database';
import { type DatabaseViewSettings } from '@databases/types/database/view';

type CreateSelectionsDatabaseSnapshotGetterOptions = {
  getState: () => DefaultRootState;
  getViewSettings: () => DatabaseViewSettings;
  selectDatabase: (state: DefaultRootState, options: { contentId: string }) => Database;
  applyView: (
    savedDatabase: Database,
    database: Database,
    viewSettings: DatabaseViewSettings,
    state: DefaultRootState,
  ) => DatabaseSnapshot;
};

type CachedDatabaseSnapshot = {
  contentId: string;
  state: DefaultRootState;
  viewSettings: DatabaseViewSettings;
  snapshot: DatabaseSnapshot;
};

/**
 * Creates the view-aware database snapshot getter used by the selections adapter.
 *
 * Databases application state is already the Yjs-backed saved database, so selections do
 * not need the shared draft selector's empty-changeset projection. The single-entry cache
 * preserves that selector's state and view-settings invalidation contract without retaining
 * snapshots for recycled database identities.
 */
export function createSelectionsDatabaseSnapshotGetter({
  getState,
  getViewSettings,
  selectDatabase,
  applyView,
}: CreateSelectionsDatabaseSnapshotGetterOptions) {
  let cached: CachedDatabaseSnapshot | undefined;

  return (contentId: string): DatabaseSnapshot => {
    const state = getState();
    const viewSettings = getViewSettings();

    if (
      cached?.contentId === contentId &&
      cached.state === state &&
      cached.viewSettings === viewSettings
    ) {
      return cached.snapshot;
    }

    const database = selectDatabase(state, { contentId });
    const snapshot = applyView(database, database, viewSettings, state);

    cached = { contentId, state, viewSettings, snapshot };

    return snapshot;
  };
}
